import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { inputClass, btnPrimary, btnGhost } from "@/components/Brand";

const Shell = ({ children, onClose, testId, danger = false }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center px-5">
      <div data-testid={testId}
        className={`relative w-full max-w-md bg-[#0F0F0F] p-8 fade-up border ${
          danger ? "border-[#8B0000]/40" : "border-[#2A2A2A]"
        }`}>
        <button data-testid="dialog-close-btn" onClick={onClose} aria-label="Close"
          className="absolute top-4 right-4 text-[#5A5A5A] hover:text-[#D4AF37] transition-colors">
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  );
};

/** Styled replacement for window.prompt. */
export const PromptDialog = ({ title, label, defaultValue = "", confirmLabel, onConfirm, onClose, testId = "prompt-dialog" }) => {
  const { t } = useI18n();
  const [value, setValue] = useState(defaultValue);

  return (
    <Shell onClose={onClose} testId={testId}>
      <p className="eyebrow text-[#D4AF37]">{title}</p>
      <label className="block eyebrow mt-6 mb-1">{label}</label>
      <input data-testid="prompt-dialog-input" autoFocus value={value} className={inputClass}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && value.trim() && onConfirm(value.trim())} />
      <div className="mt-7 flex flex-wrap gap-3">
        <button data-testid="prompt-dialog-confirm" disabled={!value.trim()}
          onClick={() => onConfirm(value.trim())} className={btnPrimary}>
          {confirmLabel || t("save")}
        </button>
        <button data-testid="prompt-dialog-cancel" onClick={onClose} className={btnGhost}>{t("cancel")}</button>
      </div>
    </Shell>
  );
};

/** Styled replacement for window.confirm. */
export const ConfirmDialog = ({ title, body, confirmLabel, onConfirm, onClose, testId = "confirm-dialog" }) => {
  const { t } = useI18n();

  return (
    <Shell onClose={onClose} testId={testId} danger>
      <p className="eyebrow text-[#8B0000]">{title}</p>
      {body && <p className="mt-5 text-sm text-[#A8A8A8] leading-relaxed" data-testid="confirm-dialog-body">{body}</p>}
      <div className="mt-7 flex flex-wrap gap-3">
        <button data-testid="confirm-dialog-confirm" onClick={onConfirm}
          className="inline-flex items-center gap-2 bg-[#8B0000] text-white px-5 py-3 text-[10px] tracking-[0.18em] uppercase font-semibold hover:bg-[#A50F0F] transition-colors">
          {confirmLabel || t("remove")}
        </button>
        <button data-testid="confirm-dialog-cancel" onClick={onClose} className={btnGhost}>{t("cancel")}</button>
      </div>
    </Shell>
  );
};

export const DialogShell = Shell;
